import { motion } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { heroVideo, heroVideoPoster } from '@/assets/videos';
import { ROUTES } from '@/constants/routes';
import { DEPLOY_AI_TEAM_CTA } from '@/constants/site-copy';
import { cn } from '@/lib/tw-merge/cn';

const IntegrationsLogoMarquee = lazy(
  () =>
    import('@/pages/site/integrations-page/components/integrations-logo-marquee')
);

interface CtaButton {
  label: string;
  to?: string;
  onClick?: () => void;
}

export type HeroDescriptionSize = 'lg' | 'md' | 'sm';

const DESCRIPTION_SIZE_CLASS: Record<HeroDescriptionSize, string> = {
  lg: 'text-lg leading-[28px] sm:text-2xl sm:leading-[38px]',
  md: 'text-base leading-[24px] sm:text-xl sm:leading-[38px]',
  sm: 'text-sm leading-[22px] sm:text-lg sm:leading-[30px]',
};

interface HeroProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  bgVideo?: string;
  /**
   * Optional WebM (VP9) sibling source. When provided it's listed first so
   * Chrome/Firefox/Edge use it (typically 30–40% smaller than mp4); Safari
   * automatically falls through to the mp4. Defaults to undefined.
   */
  bgVideoWebm?: string;
  bgImage?: string;
  /**
   * Poster image shown instantly while the bgVideo is buffering. Prevents
   * the black flash on first paint. Pass the suite's WebP avatar when
   * available so it's reused from cache.
   */
  bgPoster?: string;
  backgroundOverlayClassName?: string;
  backgroundMediaVisibleClassName?: string;
  showVideoAudioControl?: boolean;
  /** Play the first video pass with sound, then continue muted. */
  playVideoWithSound?: boolean;
  primaryCta?: CtaButton;
  secondaryCta?: CtaButton;
  showPartnerLogos?: boolean;
  videoClassName?: string;
  /** Optional pill above the title, e.g. "Running across 3 agents". */
  badge?: React.ReactNode;
  /** Typography scale for `description` (sm breakpoint and up use the named step). */
  descriptionSize?: HeroDescriptionSize;
  /** Optional custom className for the title element. */
  titleClassName?: string;
  /** Optional content rendered below the primary/secondary CTAs. */
  afterCtas?: React.ReactNode;
}

interface HeroBackgroundProps {
  bgVideo?: string;
  bgVideoWebm?: string;
  bgImage?: string;
  bgPoster?: string;
  backgroundOverlayClassName?: string;
  backgroundMediaVisibleClassName?: string;
  videoClassName?: string;
  isMuted: boolean;
  onVideoTimeUpdate: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
}

function HeroBackground({
  bgVideo,
  bgVideoWebm,
  bgImage,
  bgPoster,
  backgroundOverlayClassName,
  backgroundMediaVisibleClassName,
  videoClassName,
  isMuted,
  onVideoTimeUpdate,
  videoRef,
}: HeroBackgroundProps) {
  const resolvedBgVideo = bgVideo ?? heroVideo;
  const resolvedBgPoster = bgPoster ?? heroVideoPoster;
  const visibleMediaClassName = backgroundMediaVisibleClassName ?? 'opacity-60';
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    if (bgImage || !resolvedBgVideo) return;

    setIsVideoReady(false);
  }, [bgImage, resolvedBgVideo]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {bgImage ? (
        <img
          src={bgImage}
          alt=""
          aria-hidden="true"
          loading="eager"
          {...{ fetchpriority: 'high' }}
          decoding="async"
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full select-none object-cover',
            visibleMediaClassName
          )}
        />
      ) : (
        <>
          {resolvedBgPoster && (
            <img
              src={resolvedBgPoster}
              alt=""
              aria-hidden="true"
              loading="eager"
              {...{ fetchpriority: 'high' }}
              decoding="async"
              className={cn(
                'pointer-events-none absolute inset-0 h-full w-full select-none object-cover transition-opacity duration-300',
                isVideoReady ? 'opacity-0' : visibleMediaClassName,
                videoClassName
              )}
            />
          )}
          <video
            ref={videoRef}
            key={resolvedBgVideo}
            muted={isMuted}
            loop
            playsInline
            controls={false}
            preload="auto"
            poster={resolvedBgPoster}
            aria-hidden="true"
            onLoadedData={() => setIsVideoReady(true)}
            onPlaying={() => setIsVideoReady(true)}
            onTimeUpdate={onVideoTimeUpdate}
            className={cn(
              'absolute inset-0 h-full w-full object-cover transition-opacity duration-300',
              isVideoReady ? visibleMediaClassName : 'opacity-0',
              videoClassName
            )}
          >
            {bgVideoWebm && <source src={bgVideoWebm} type="video/webm" />}
            <source src={resolvedBgVideo} type="video/mp4" />
          </video>
        </>
      )}
      <div
        aria-hidden="true"
        className={cn(
          'z-999 pointer-events-none absolute inset-0',
          backgroundOverlayClassName ?? 'bg-[#00000029]'
        )}
      />
    </div>
  );
}

function VideoAudioControl({
  isMuted,
  onToggle,
}: {
  isMuted: boolean;
  onToggle: () => void;
}) {
  const label = isMuted ? 'Unmute' : 'Mute';

  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex h-8 items-center gap-2 rounded-full bg-[#00000045] px-4 text-[11px] font-medium text-white backdrop-blur-sm transition-colors hover:bg-black/40 sm:h-10"
      aria-label={`${label} video sound`}
    >
      <span>{label}</span>
      {isMuted ? (
        <VolumeX className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

export default function Hero({
  title,
  subtitle,
  description,
  bgVideo,
  bgVideoWebm,
  bgImage,
  bgPoster,
  backgroundOverlayClassName,
  backgroundMediaVisibleClassName,
  showVideoAudioControl = false,
  playVideoWithSound = false,
  primaryCta,
  secondaryCta,
  showPartnerLogos = true,
  videoClassName,
  badge,
  descriptionSize = 'lg',
  titleClassName,
  afterCtas,
}: HeroProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideoVisibleRef = useRef(false);
  const hasCompletedInitialPlaybackRef = useRef(false);
  const [isVideoMuted, setIsVideoMuted] = useState(!playVideoWithSound);
  const resolvedTitle = title ?? (
    <>
      AI Workforce.
      <br />
      Mission Ready.
    </>
  );

  const [shouldMountPartnerLogos, setShouldMountPartnerLogos] =
    useState<boolean>(false);

  useEffect(() => {
    if (!showPartnerLogos) return;

    const timeout = window.setTimeout(() => {
      window.requestAnimationFrame(() => setShouldMountPartnerLogos(true));
    }, 1200);

    return () => window.clearTimeout(timeout);
  }, [showPartnerLogos]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let active = true;
    const syncPlayback = () => {
      if (isVideoVisibleRef.current && document.visibilityState === 'visible') {
        void video.play().catch(async error => {
          if (
            !active ||
            !isVideoVisibleRef.current ||
            document.visibilityState !== 'visible' ||
            video.muted ||
            !(error instanceof DOMException) ||
            error.name !== 'NotAllowedError'
          )
            return;
          video.muted = true;
          setIsVideoMuted(true);
          await video.play().catch(() => undefined);
        });
        return;
      }

      video.pause();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVideoVisibleRef.current = entry.isIntersecting;
        syncPlayback();
      },
      { threshold: 0.1 }
    );

    observer.observe(video);
    document.addEventListener('visibilitychange', syncPlayback);

    return () => {
      active = false;
      observer.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
      video.pause();
      video.currentTime = 0;
      isVideoVisibleRef.current = false;
    };
  }, [bgVideo]);

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (
      !video ||
      !playVideoWithSound ||
      hasCompletedInitialPlaybackRef.current ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0 ||
      video.duration - video.currentTime > 0.3
    ) {
      return;
    }

    hasCompletedInitialPlaybackRef.current = true;
    video.muted = true;
    setIsVideoMuted(true);
  };

  const primary: CtaButton = primaryCta ?? {
    label: DEPLOY_AI_TEAM_CTA,
    to: ROUTES.SIGNUP,
  };

  const secondary: CtaButton = secondaryCta ?? {
    label: 'Schedule a Demo',
    to: ROUTES.CONTACT,
  };

  const toggleVideoAudio = () => {
    const video = videoRef.current;
    if (!video) return;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsVideoMuted(nextMuted);
    if (video.paused) {
      void video.play().catch(() => {
        video.muted = true;
        setIsVideoMuted(true);
      });
    }
  };

  return (
    <section className="[@media(max-height:750px)]:h-172.5 min-h-162.5 relative h-[95vh] w-full bg-black text-white [@media(max-height:750px)]:min-h-0">
      {/*
        Background media is clipped inside this overflow-hidden wrapper so the
        navbar's hover dropdown can extend beyond the hero's bottom edge
        without being cut off. Don't move the navbar back inside this wrapper
        unless you also handle dropdown clipping.
      */}
      <HeroBackground
        bgVideo={bgVideo}
        bgVideoWebm={bgVideoWebm}
        bgImage={bgImage}
        bgPoster={bgPoster}
        backgroundOverlayClassName={backgroundOverlayClassName}
        backgroundMediaVisibleClassName={backgroundMediaVisibleClassName}
        videoClassName={videoClassName}
        isMuted={isVideoMuted}
        onVideoTimeUpdate={handleVideoTimeUpdate}
        videoRef={videoRef}
      />

      <div
        className={cn(
          'app-container relative z-10 flex h-full flex-col items-start justify-center gap-7 py-20 text-left [@media(max-height:760px)]:gap-4 [@media(max-height:760px)]:py-10 [@media(max-height:900px)]:gap-5 [@media(max-height:900px)]:py-14',
          playVideoWithSound && 'max-md:justify-end max-md:pb-16 max-md:pt-24',
          (showVideoAudioControl || afterCtas) &&
            'translate-y-4 sm:translate-y-6 [@media(max-height:900px)]:translate-y-3'
        )}
      >
        {badge && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border-white/15 bg-white/10 hidden h-[27.64px] items-center gap-2 rounded-full border px-3 text-[10px] font-medium text-stroke-100 backdrop-blur-sm sm:h-10 sm:gap-2.5 sm:px-4 sm:text-sm [@media(max-height:760px)]:sm:h-8 [@media(max-height:760px)]:sm:text-xs"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary sm:h-2 sm:w-2" />
            {badge}
          </motion.div>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={cn(
            'max-w-5xl font-fraunces font-bold tracking-[-1%] text-white',
            // Mobile: clamp keeps line-height between 42px and 110px while
            // allowing it to scale with 7% of the viewport width.
            'text-[32px] leading-[clamp(42px,7vw,110px)]',
            // Tablet and desktop: The clamp values provide a minimum, fluid, and maximum
            // font size as the viewport becomes wider.
            'sm:text-[56px] sm:leading-[clamp(33.6px,5.6vw,88px)]',
            'md:text-[clamp(38.4px,5.52vw,72px)]',
            // Short tablet/desktop screens: reduce both the title and its
            // line-height so the rest of the hero content remains visible.
            '[@media(min-width:640px)_and_(max-height:850px)]:!text-[clamp(32px,3.52vw,48px)]',
            '[@media(min-width:640px)_and_(max-height:850px)]:!leading-[clamp(36.8px,4vw,52.8px)]',
            // Keep medium-height screens capped until the large-monitor scale
            // begins at a viewport height of 1000px.
            '[@media(min-width:640px)_and_(max-height:999px)]:text-[clamp(35.2px,4vw,57.6px)]',
            '[@media(min-width:640px)_and_(max-height:999px)]:leading-[clamp(40px,4.64vw,62.4px)]',
            // Large desktop monitors: use the full display scale only when
            // both width and height exceed typical laptop viewports.
            '[@media(min-width:1920px)_and_(min-height:1000px)]:!text-[80px]',
            '[@media(min-width:1920px)_and_(min-height:1000px)]:!leading-[100px]',
            titleClassName
          )}
        >
          {resolvedTitle}
        </motion.h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={cn(
              'max-w-[306px] text-xl font-medium leading-[32px] text-white',
              'sm:max-w-3xl md:text-[32px]',
              // Keep the subtitle compact on short tablet and desktop screens,
              // including laptops with a viewport height of 850px or less.
              '[@media(min-width:640px)_and_(max-height:850px)]:!text-2xl',
              '[@media(min-width:640px)_and_(max-height:850px)]:!leading-[30px]'
            )}
          >
            {subtitle}
          </motion.p>
        )}

        {/* Divider — width matches the description below it
            (max-w-[306px] on mobile, max-w-[754px] on sm+), so the
            line never overshoots the paragraph beneath it. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex w-full max-w-[306px] items-center gap-4 sm:max-w-[620px]"
        >
          <hr className="border-white/60 flex-1 border-t" />
        </motion.div>

        {description && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className={cn(
              'text-white/80 max-w-[306px] sm:max-w-[650px]',
              DESCRIPTION_SIZE_CLASS[descriptionSize],
              // Compact laptop viewports use a smaller description with a
              // proportional line-height to preserve vertical space.
              '[@media(min-width:640px)_and_(max-height:850px)]:!text-lg',
              '[@media(min-width:640px)_and_(max-height:850px)]:!leading-7'
            )}
          >
            {description}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-5 flex flex-wrap items-center gap-4 sm:mt-10 sm:gap-6 [@media(max-height:760px)]:gap-5 [@media(max-height:900px)]:mt-3"
        >
          {primary.to ? (
            <Link
              to={primary.to}
              className="flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-white transition-opacity hover:opacity-90 sm:h-[60px] sm:text-base md:min-w-[220px] lg:text-lg [@media(max-height:760px)]:sm:h-12 [@media(max-height:760px)]:lg:text-base"
            >
              {primary.label}
            </Link>
          ) : (
            <button
              onClick={primary.onClick}
              className="flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-white transition-opacity hover:opacity-90 sm:h-[60px] sm:text-base md:min-w-[220px] lg:text-lg [@media(max-height:760px)]:sm:h-12 [@media(max-height:760px)]:lg:text-base"
            >
              {primary.label}
            </button>
          )}

          {secondary.to ? (
            <Link
              to={secondary.to}
              className="flex h-11 items-center border-b border-white px-2 py-4 text-base font-medium text-white transition-opacity hover:opacity-80 sm:h-[56px] [@media(max-height:760px)]:sm:h-12"
            >
              {secondary.label}
            </Link>
          ) : (
            <button
              onClick={secondary.onClick}
              className="flex h-11 items-center border-b border-white px-2 py-4 text-base font-medium text-white transition-opacity hover:opacity-80 sm:h-[56px] [@media(max-height:760px)]:sm:h-12"
            >
              {secondary.label}
            </button>
          )}
        </motion.div>

        {showVideoAudioControl && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative"
          >
            <VideoAudioControl
              isMuted={isVideoMuted}
              onToggle={toggleVideoAudio}
            />
          </motion.div>
        )}

        {afterCtas && (
          <div className="relative top-4 -mt-7 h-0 sm:top-0 sm:-mt-3 sm:h-auto [@media(min-width:640px)_and_(max-height:900px)]:-mt-2">
            {afterCtas}
          </div>
        )}
      </div>

      {/* Partner Logos — pulled from the live integrations API and
          rendered as white SVGs (Simple Icons) so they sit on the
          hero video. */}
      {showPartnerLogos && shouldMountPartnerLogos && (
        <div className="absolute bottom-10 left-0 right-0 [@media(max-height:760px)]:bottom-4 [@media(max-height:900px)]:bottom-6">
          <div className="app-container">
            <Suspense fallback={null}>
              <IntegrationsLogoMarquee isHero gapClass="gap-12 md:gap-16" />
            </Suspense>
          </div>
        </div>
      )}
    </section>
  );
}
